<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import parsePhoneNumber from 'libphonenumber-js'
import { useForm } from 'vee-validate'
import { useId } from 'reka-ui'
import { z } from 'zod'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

export interface ManualEntryValues {
  first_name: string
  last_name: string
  email: string | null
  phone: string | null
}

export interface RoleOption {
  value: string
  label: string
}

const props = withDefaults(defineProps<{
  roles: RoleOption[]
  defaultRole: string
  mode?: 'email' | 'emailOrPhone'
  initialContact?: string
}>(), {
  mode: 'email',
  initialContact: '',
})

const emit = defineEmits<{
  submit: [values: ManualEntryValues, role: string]
  cancel: []
}>()

const role = defineModel<string>('role', { default: '' })

// Email-only schema
const emailSchema = toTypedSchema(z.object({
  first_name: z.string().min(1, 'Required').max(255),
  last_name: z.string().min(1, 'Required').max(255),
  contact: z.string().min(1, 'Required').email('Enter a valid email'),
}))

// Email or phone schema
const phoneFieldSchema = z.string().min(1, 'Enter a valid email or phone').transform((value, ctx) => {
  try {
    const phoneNumber = parsePhoneNumber(value, { defaultCountry: 'US' })
    if (!phoneNumber?.isValid()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid email or phone' })
      return z.NEVER
    }
    return phoneNumber.number as string
  } catch {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid email or phone' })
    return z.NEVER
  }
})
const emailFieldSchema = z.string().min(1, 'Enter a valid email or phone').email('Enter a valid email or phone')
const emailOrPhoneField = z.union([emailFieldSchema, phoneFieldSchema]).refine(
  v => v !== null && v !== undefined && v !== '',
  { message: 'Enter a valid email or phone' },
)

const emailOrPhoneSchema = toTypedSchema(z.object({
  first_name: z.string().min(1, 'Required').max(255),
  last_name: z.string().min(1, 'Required').max(255),
  contact: emailOrPhoneField,
}))

const validationSchema = props.mode === 'emailOrPhone' ? emailOrPhoneSchema : emailSchema
const contactLabel = props.mode === 'emailOrPhone' ? 'Email or phone' : 'Email'

const { defineField, handleSubmit, errors } = useForm({
  validationSchema,
  initialValues: { first_name: '', last_name: '', contact: props.initialContact },
})

const [firstName, firstNameAttrs] = defineField('first_name')
const [lastName, lastNameAttrs] = defineField('last_name')
const [contact, contactAttrs] = defineField('contact')

const firstNameId = useId()
const lastNameId = useId()
const contactId = useId()

const onSubmit = handleSubmit((values) => {
  const contactVal = values.contact as string
  let email: string | null = null
  let phone: string | null = null

  if (props.mode === 'emailOrPhone' && !contactVal.includes('@')) {
    phone = contactVal
  } else {
    email = contactVal
  }

  emit('submit', {
    first_name: values.first_name,
    last_name: values.last_name,
    email,
    phone,
  }, role.value || props.defaultRole)
})

const onCancel = () => {
  role.value = props.defaultRole
  emit('cancel')
}

// Initialize role
if (!role.value) {
  role.value = props.defaultRole
}
</script>

<template>
  <div class="space-y-2 rounded-md border p-3">
    <p class="text-sm font-medium">Add new user</p>
    <div class="space-y-1">
      <label :for="firstNameId" class="text-sm font-medium" :class="errors.first_name && 'text-destructive'">First name</label>
      <Input :id="firstNameId" v-model="firstName" v-bind="firstNameAttrs" data-testid="invite-first-name-input" />
      <p v-if="errors.first_name" class="text-sm font-medium text-destructive">{{ errors.first_name }}</p>
    </div>
    <div class="space-y-1">
      <label :for="lastNameId" class="text-sm font-medium" :class="errors.last_name && 'text-destructive'">Last name</label>
      <Input :id="lastNameId" v-model="lastName" v-bind="lastNameAttrs" data-testid="invite-last-name-input" />
      <p v-if="errors.last_name" class="text-sm font-medium text-destructive">{{ errors.last_name }}</p>
    </div>
    <div class="space-y-1">
      <label :for="contactId" class="text-sm font-medium" :class="errors.contact && 'text-destructive'">{{ contactLabel }}</label>
      <Input :id="contactId" v-model="contact" v-bind="contactAttrs" data-testid="invite-email-phone-input" />
      <p v-if="errors.contact" class="text-sm font-medium text-destructive">{{ errors.contact }}</p>
    </div>
    <div>
      <Tabs :model-value="role" @update:model-value="v => role = String(v)">
        <TabsList class="h-7 p-0.5">
          <TabsTrigger
            v-for="r in roles"
            :key="r.value"
            :value="r.value"
            class="h-6 px-2 text-xs"
          >
            {{ r.label }}
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
    <div class="flex gap-2 justify-end">
      <Button type="button" variant="ghost" size="sm" @click="onCancel">Cancel</Button>
      <Button type="button" size="sm" @click="onSubmit">Add</Button>
    </div>
  </div>
</template>
